import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconText from '../icons/IconText';
import IconButton from '../icons/IconButton';
import { ICON } from '../../icons/iconIds';
import SelkirkIcon from '../icons/SelkirkIcon';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { documentDesignService } from '../../services/documentDesignService';
import { formatBytes } from './slotFitness';
import { formatRelative } from './BrandLibraryTab';
import BlockActions from './BlockActions';
import TablePreview from './TablePreview';
import LayoutView from './LayoutView';
import DocumentPreview from './DocumentPreview';
import {
  BLOCK_TYPE_LABELS,
  ISSUE_LABELS,
  STATUS_LABELS,
  confidenceClass,
  effectiveBlock,
  isBusy,
} from './reviewLabels';

const POLL_MS = 4000;
const CONTENT_PAGE = 200;
const SEVERITY_ICON = { error: ICON.ddQaBlocked, warning: ICON.uiWarning, info: ICON.uiTip };
const VIEWS = [
  { id: 'issues', label: 'Issues' },
  { id: 'outline', label: 'Outline' },
  { id: 'figures', label: 'Figures' },
  { id: 'tables', label: 'Tables' },
  { id: 'content', label: 'All content' },
  { id: 'layout', label: 'Layout & photos' },
  { id: 'details', label: 'Details' },
];
const LOCAL_VIEWS = new Set(['details', 'layout']);
const BULK_OPTIONS = [
  { value: 'keep', label: 'Confirm all' },
  { value: 'set_body', label: 'All → body text' },
  { value: 'exclude', label: 'Exclude all' },
  { value: 'clear', label: 'Clear all overrides' },
];
const META_FIELDS = [
  ['title', 'Title'],
  ['project', 'Project'],
  ['report_type', 'Report type'],
  ['report_date', 'Report date'],
  ['company', 'Company'],
  ['creator', 'Author'],
  ['last_modified_by', 'Last modified by'],
];

function useOverrides() {
  const [map, setMap] = useState({});
  const resolve = useCallback(
    (item) => (Object.prototype.hasOwnProperty.call(map, item.block_key) ? map[item.block_key] : item.override),
    [map],
  );
  return [resolve, setMap];
}

const DocumentReview = ({ documentId, onBack, onUploadVersion, uploading, onDelete }) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('issues');
  const [versionNo, setVersionNo] = useState(null);
  const [viewData, setViewData] = useState({});
  const [viewLoading, setViewLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [resolveOverride, setOverrideMap] = useOverrides();
  const [contentFilter, setContentFilter] = useState({ type: '', q: '', lowConfidence: false });
  const [severity, setSeverity] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const versionInput = useRef(null);

  const loadDoc = useCallback(async () => {
    try {
      const data = await documentDesignService.getDocument(documentId);
      setDoc(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load document');
      return null;
    }
  }, [documentId]);

  useEffect(() => {
    loadDoc();
  }, [loadDoc]);

  const version = useMemo(() => {
    if (!doc) return null;
    if (versionNo) return doc.versions.find((v) => v.version_no === versionNo) || null;
    return doc.current || doc.versions[0] || null;
  }, [doc, versionNo]);
  const latest = doc?.versions?.[0];
  const busy = isBusy(latest?.status);
  const parsed = version?.status === 'parsed';

  useEffect(() => {
    if (!busy) return undefined;
    const handle = setInterval(async () => {
      const data = await loadDoc();
      if (data && !isBusy(data.versions?.[0]?.status)) {
        setVersionNo(null);
        setViewData({});
        setOverrideMap({});
      }
    }, POLL_MS);
    return () => clearInterval(handle);
  }, [busy, loadDoc, setOverrideMap]);

  const versionParam = versionNo || undefined;

  const loadView = useCallback(
    async (which, { append = false } = {}) => {
      if (!parsed) return;
      setViewLoading(true);
      try {
        let data;
        if (which === 'issues') {
          data = await documentDesignService.listIssues(documentId, { version: versionParam });
        } else if (which === 'outline') {
          data = (await documentDesignService.listBlocks(documentId, {
            version: versionParam, type: 'title,heading', include_data: 'false', limit: 2000,
          })).items;
        } else if (which === 'figures') {
          data = await documentDesignService.listAssets(documentId, { version: versionParam });
        } else if (which === 'tables') {
          data = (await documentDesignService.listBlocks(documentId, { version: versionParam, type: 'table', limit: 500 })).items;
        } else if (which === 'content') {
          const prev = append ? viewData.content : null;
          const result = await documentDesignService.listBlocks(documentId, {
            version: versionParam,
            type: contentFilter.type || undefined,
            q: contentFilter.q.trim() || undefined,
            low_confidence: contentFilter.lowConfidence,
            limit: CONTENT_PAGE,
            offset: prev ? prev.items.length : 0,
          });
          data = { items: prev ? [...prev.items, ...result.items] : result.items, total: result.total };
        } else {
          return;
        }
        setViewData((d) => ({ ...d, [which]: data }));
      } catch (err) {
        setError(err.message || 'Failed to load');
      } finally {
        setViewLoading(false);
      }
    },
    [documentId, parsed, versionParam, contentFilter, viewData.content],
  );

  useEffect(() => {
    if (parsed && !LOCAL_VIEWS.has(view) && viewData[view] === undefined) loadView(view);
  }, [parsed, view, viewData, loadView]);

  useEffect(() => {
    setViewData((d) => ({ ...d, content: undefined }));
  }, [contentFilter]);

  const applyOverrides = async (overrides) => {
    setBusyKey(overrides.length === 1 ? overrides[0].block_key : '*');
    try {
      await documentDesignService.setOverrides(documentId, overrides);
      setOverrideMap((m) => {
        const next = { ...m };
        overrides.forEach((o) => {
          next[o.block_key] = o.action === 'clear' ? null : { action: o.action, level: o.level ?? null };
        });
        return next;
      });
    } catch (err) {
      setError(`Could not save: ${err.message}`);
    } finally {
      setBusyKey(null);
    }
  };
  const applyOne = (o) => applyOverrides([o]);

  const reparse = async () => {
    try {
      await documentDesignService.reparseDocument(documentId, version?.version_no);
      setViewData({});
      await loadDoc();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveField = async (key, value) => {
    try {
      setDoc(await documentDesignService.updateDocument(documentId, { [key]: value }));
    } catch (err) {
      setError(`Could not save ${key}: ${err.message}`);
    }
  };

  if (!doc) {
    return error ? (
      <div className="dd-error-inline">{error} <Button size="sm" variant="ghost" onClick={onBack}>Back</Button></div>
    ) : (
      <div className="loading-container"><Spinner size="large" /><p>Loading document…</p></div>
    );
  }

  const stats = version?.stats || {};
  const issueCounts = stats.issues || {};

  return (
    <div className="dd-review">
      <div className="dd-review-header">
        <button type="button" className="dd-link-button" onClick={onBack}>← All documents</button>
        <div className="dd-review-title">
          <h2>{doc.title}</h2>
          <div className="dd-muted">
            {[doc.project, doc.report_type, version?.source_file_name].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="dd-header-actions">
          {doc.versions.length > 1 && (
            <select
              value={version?.version_no || ''}
              onChange={(e) => { setVersionNo(Number(e.target.value)); setViewData({}); setOverrideMap({}); }}
            >
              {doc.versions.map((v) => (
                <option key={v.id} value={v.version_no}>
                  v{v.version_no} · {STATUS_LABELS[v.status] || v.status} · {formatRelative(v.created_at)}
                </option>
              ))}
            </select>
          )}
          <Button variant="secondary" onClick={() => versionInput.current?.click()} loading={!!uploading} disabled={busy}>
            Upload new version
          </Button>
          <input
            ref={versionInput}
            type="file"
            accept=".docx"
            hidden
            onChange={async (e) => {
              const file = e.target.files[0];
              e.target.value = '';
              if (file) {
                await onUploadVersion(file, documentId);
                setVersionNo(null);
                await loadDoc();
              }
            }}
          />
          <Button onClick={() => setPreviewing(true)} disabled={!parsed}>Preview &amp; PDF</Button>
        </div>
      </div>

      {error && <div className="dd-error-inline">{error} <IconButton icon={ICON.ddClose} onClick={() => setError(null)} /></div>}

      {busy && (
        <Card className="dd-setup-card dd-parsing-card">
          <Spinner size="small" />
          <div>
            <h3>{STATUS_LABELS[latest.status]} v{latest.version_no}</h3>
            <p className="dd-muted">Extracting structure, figures and tables from {latest.source_file_name} ({formatBytes(latest.source_size_bytes)}). This page updates automatically.</p>
          </div>
        </Card>
      )}

      {version?.status === 'error' && (
        <Card className="dd-setup-card">
          <h3>Parsing failed</h3>
          <p className="dd-muted">{version.error}</p>
          <Button size="sm" variant="secondary" onClick={reparse}>Retry parsing</Button>
        </Card>
      )}

      {parsed && (
        <>
          <Card className="summary-card">
            <div className="summary-grid">
              <Stat label="Est. pages" value={stats.estimated_pages} hint={stats.source_pages ? `Word: ${stats.source_pages}` : null} />
              <Stat label="Words" value={stats.words?.toLocaleString()} />
              <Stat label="Headings" value={stats.headings} />
              <Stat label="Figures" value={stats.figures} />
              <Stat label="Tables" value={stats.tables} />
              <Stat label="Issues" value={(issueCounts.error || 0) + (issueCounts.warning || 0)} hint={`${issueCounts.info || 0} notes`} />
              <Stat label="Reviewed" value={doc.override_count} hint="blocks overridden" />
            </div>
          </Card>

          <div className="dd-view-tabs" role="tablist">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={view === v.id}
                className={`dd-filter-chip ${view === v.id ? 'on' : ''}`}
                onClick={() => setView(v.id)}
              >
                {v.label}
                {v.id === 'issues' && (issueCounts.error || issueCounts.warning) ? ` (${(issueCounts.error || 0) + (issueCounts.warning || 0)})` : ''}
              </button>
            ))}
          </div>

          <Card className="content-card">
            {view === 'layout' ? (
              <LayoutView documentId={documentId} versionNo={versionNo} />
            ) : viewLoading && viewData[view] === undefined ? (
              <div className="loading-container"><Spinner size="large" /></div>
            ) : view === 'issues' ? (
              <IssuesView
                issues={viewData.issues || []}
                severity={severity}
                setSeverity={setSeverity}
                resolveOverride={resolveOverride}
                onApply={applyOne}
                onBulk={applyOverrides}
                busyKey={busyKey}
              />
            ) : view === 'outline' ? (
              <OutlineView blocks={viewData.outline || []} resolveOverride={resolveOverride} onApply={applyOne} busyKey={busyKey} />
            ) : view === 'figures' ? (
              <FiguresView assets={viewData.figures || []} />
            ) : view === 'tables' ? (
              <TablesView tables={viewData.tables || []} resolveOverride={resolveOverride} onApply={applyOne} busyKey={busyKey} />
            ) : view === 'content' ? (
              <ContentView
                data={viewData.content}
                filter={contentFilter}
                setFilter={setContentFilter}
                onMore={() => loadView('content', { append: true })}
                loading={viewLoading}
                resolveOverride={resolveOverride}
                onApply={applyOne}
                busyKey={busyKey}
              />
            ) : (
              <DetailsView doc={doc} version={version} onSave={saveField} onReparse={reparse} onDelete={onDelete} />
            )}
          </Card>
        </>
      )}

      {previewing && (
        <DocumentPreview
          documentId={documentId}
          versionNo={versionNo}
          title={doc.title}
          sourceFileName={version?.source_file_name}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  );
};

const Stat = ({ label, value, hint }) => (
  <div className="stat-item">
    <span className="stat-label">{label}</span>
    <span className="stat-value">{value ?? '—'}</span>
    {hint && <span className="stat-label">{hint}</span>}
  </div>
);

const BlockText = ({ block, max = 300 }) => {
  const text = block.text || block.block_text || '';
  return <span className={block.excluded ? 'dd-excluded' : undefined}>{text.length > max ? `${text.slice(0, max)}…` : text || <em className="dd-muted">(no text)</em>}</span>;
};

const IssuesView = ({ issues, severity, setSeverity, resolveOverride, onApply, onBulk, busyKey }) => {
  const filtered = severity ? issues.filter((i) => i.severity === severity) : issues;
  const groups = [];
  const byCode = {};
  filtered.forEach((i) => {
    if (!byCode[i.code]) {
      byCode[i.code] = { code: i.code, severity: i.severity, items: [] };
      groups.push(byCode[i.code]);
    }
    byCode[i.code].items.push(i);
  });
  const counts = issues.reduce((acc, i) => ({ ...acc, [i.severity]: (acc[i.severity] || 0) + 1 }), {});

  if (issues.length === 0) {
    return <div className="no-data"><div className="no-data-icon"><SelkirkIcon name={ICON.uiSuccess} size={48} decorative /></div><h3>No issues found</h3></div>;
  }

  return (
    <>
      <div className="dd-chip-row">
        {['', 'error', 'warning', 'info'].map((s) => (
          <button key={s || 'all'} type="button" className={`dd-filter-chip ${severity === s ? 'on' : ''}`} onClick={() => setSeverity(s)}>
            {s ? `${SEVERITY_ICON[s]} ${s} (${counts[s] || 0})` : `All (${issues.length})`}
          </button>
        ))}
      </div>
      {groups.map((g) => {
        const keyed = g.items.filter((i) => i.block_key);
        return (
          <section key={g.code} className="dd-issue-group">
            <header>
              <h3><span className={`dd-sev ${g.severity}`}>{SEVERITY_ICON[g.severity]}</span> {ISSUE_LABELS[g.code] || g.code} <span className="dd-muted">· {g.items.length}</span></h3>
              {keyed.length > 1 && (
                <select
                  defaultValue=""
                  disabled={busyKey === '*'}
                  onChange={(e) => {
                    const action = e.target.value;
                    e.target.value = '';
                    if (action) onBulk(keyed.map((i) => ({ block_key: i.block_key, action })));
                  }}
                >
                  <option value="" disabled>Apply to all {keyed.length}…</option>
                  {BULK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </header>
            <ul className="dd-issue-list">
              {g.items.slice(0, 300).map((i) => {
                const block = i.block_key ? effectiveBlock({ ...i, override: resolveOverride(i) }) : null;
                return (
                  <li key={i.id}>
                    <div className="dd-issue-body">
                      <div>{i.message}</div>
                      {block && i.block_text && (
                        <div className="dd-issue-block">
                          <span className="dd-type-badge">{BLOCK_TYPE_LABELS[block.block_type] || block.block_type}{block.level ? ` ${block.level}` : ''}</span>
                          <BlockText block={block} max={160} />
                        </div>
                      )}
                    </div>
                    {block && <BlockActions block={block} onApply={onApply} busy={busyKey === i.block_key || busyKey === '*'} />}
                  </li>
                );
              })}
              {g.items.length > 300 && <li className="dd-muted">… {g.items.length - 300} more</li>}
            </ul>
          </section>
        );
      })}
    </>
  );
};

const OutlineView = ({ blocks, resolveOverride, onApply, busyKey }) => {
  if (blocks.length === 0) {
    return <div className="no-data"><h3>No headings detected</h3><p>Use All content to mark headings, or apply Heading styles in Word and upload a new version.</p></div>;
  }
  return (
    <ul className="dd-outline">
      {blocks.map((raw) => {
        const b = effectiveBlock({ ...raw, override: resolveOverride(raw) });
        const level = b.block_type === 'heading' ? b.level || 1 : b.block_type === 'title' ? 0 : 6;
        return (
          <li key={b.seq} className={`lvl-${Math.min(level, 6)} ${b.block_type !== 'heading' && b.block_type !== 'title' ? 'demoted' : ''}`}>
            <span className={`dd-conf ${confidenceClass(Number(b.confidence))}`} title={`Confidence ${Number(b.confidence).toFixed(2)}${b.source_style ? ` · style: ${b.source_style}` : ''}`} />
            <span className="dd-outline-text">
              {b.block_type === 'title' ? <strong>{b.text}</strong> : <BlockText block={b} max={160} />}
            </span>
            <BlockActions block={b} onApply={onApply} busy={busyKey === b.block_key} />
          </li>
        );
      })}
    </ul>
  );
};

const dpiClass = (dpi) => (!dpi ? 'unknown' : dpi >= 300 ? 'good' : dpi >= 220 ? 'fair' : 'low');

const FiguresView = ({ assets }) => {
  if (assets.length === 0) return <div className="no-data"><h3>No figures</h3></div>;
  return (
    <div className="dd-media-grid dd-figure-grid">
      {assets.map((a) => (
        <a key={a.asset_key} className="dd-media-card" href={a.original_url || a.preview_url} target="_blank" rel="noreferrer">
          <div className="dd-media-thumb">
            {a.preview_url ? <img src={a.preview_url} alt={a.caption || a.asset_key} loading="lazy" /> : <span>{a.kind === 'chart' ? (<IconText icon={ICON.uiChartAnalytics} iconSize={14}>Chart</IconText>) : a.kind === 'object' ? (<IconText icon={ICON.ddAssetAttachmentPlaceholder} iconSize={14}>Object</IconText>) : (a.content_type || 'No preview')}</span>}
            <div className="dd-media-badges">
              {a.figure_number && <span className="dd-badge cover">Fig {a.figure_number}</span>}
              {a.effective_dpi ? <span className={`dd-badge dpi-${dpiClass(a.effective_dpi)}`}>{a.effective_dpi} dpi</span> : null}
            </div>
          </div>
          <div className="dd-media-meta">
            <span className="dd-media-name" title={a.caption || ''}>{a.caption || <em className="dd-muted">No caption</em>}</span>
            <span className="dd-media-sub">
              {a.width_px ? `${a.width_px}×${a.height_px}px` : a.kind}
              {a.displayed_width_in ? ` · placed ${Number(a.displayed_width_in).toFixed(1)}″ wide` : ''}
              {a.size_bytes ? ` · ${formatBytes(a.size_bytes)}` : ''}
            </span>
          </div>
        </a>
      ))}
    </div>
  );
};

const TablesView = ({ tables, resolveOverride, onApply, busyKey }) => {
  if (tables.length === 0) return <div className="no-data"><h3>No tables</h3></div>;
  return (
    <div className="dd-table-list">
      {tables.map((raw) => {
        const t = effectiveBlock({ ...raw, override: resolveOverride(raw) });
        return (
          <section key={t.seq} className={`dd-table-item ${t.excluded ? 'excluded' : ''}`}>
            <header>
              <div>
                <strong>{t.data?.figure_number ? `Table ${t.data.figure_number}` : `Table at block ${t.seq}`}</strong>
                <span className="dd-muted"> · {t.data?.row_count} rows × {t.data?.col_count} cols{t.data?.width_in ? ` · ${t.data.width_in}″ wide` : ''}</span>
                {t.role && <span className="dd-type-badge">{t.role.replace('_', ' ')}</span>}
                {t.data?.header_inferred && <span className="dd-type-badge">header inferred</span>}
                {t.data?.caption && <div className="dd-muted">{t.data.caption}</div>}
              </div>
              <BlockActions block={t} onApply={onApply} busy={busyKey === t.block_key} />
            </header>
            <TablePreview data={t.data} />
          </section>
        );
      })}
    </div>
  );
};

const ContentView = ({ data, filter, setFilter, onMore, loading, resolveOverride, onApply, busyKey }) => {
  const [q, setQ] = useState(filter.q);
  useEffect(() => {
    const h = setTimeout(() => { if (q !== filter.q) setFilter((f) => ({ ...f, q })); }, 400);
    return () => clearTimeout(h);
  }, [q, filter.q, setFilter]);

  return (
    <>
      <div className="dd-toolbar">
        <input type="search" className="dd-search" placeholder="Search text" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}>
          <option value="">All block types</option>
          {Object.entries(BLOCK_TYPE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <label className="dd-inline-check">
          <input type="checkbox" checked={filter.lowConfidence} onChange={(e) => setFilter((f) => ({ ...f, lowConfidence: e.target.checked }))} />
          Low confidence only
        </label>
      </div>
      {!data ? (
        <div className="loading-container"><Spinner size="large" /></div>
      ) : (
        <>
          <p className="dd-muted">{data.total.toLocaleString()} blocks</p>
          <ol className="dd-block-list">
            {data.items.map((raw) => {
              const b = effectiveBlock({ ...raw, override: resolveOverride(raw) });
              return (
                <li key={b.seq} className={`type-${b.block_type} ${b.excluded ? 'excluded' : ''}`}>
                  <span className="dd-seq">{b.seq}</span>
                  <span className={`dd-conf ${confidenceClass(Number(b.confidence))}`} title={`Confidence ${Number(b.confidence).toFixed(2)}${b.source_style ? ` · style: ${b.source_style}` : ''}`} />
                  <span className="dd-type-badge">{BLOCK_TYPE_LABELS[b.block_type] || b.block_type}{b.level ? ` ${b.level}` : ''}{b.role ? ` · ${b.role}` : ''}</span>
                  <span className="dd-block-text">
                    {b.number_label && b.block_type === 'list_item' ? `${b.number_label} ` : ''}
                    <BlockText block={b} />
                  </span>
                  {b.block_type !== 'page_break' && <BlockActions block={b} onApply={onApply} busy={busyKey === b.block_key} />}
                </li>
              );
            })}
          </ol>
          {data.items.length < data.total && (
            <div className="dd-load-more">
              <Button variant="secondary" onClick={onMore} loading={loading}>Load more ({data.total - data.items.length} remaining)</Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

const DetailsView = ({ doc, version, onSave, onReparse, onDelete }) => {
  const [form, setForm] = useState({ title: doc.title || '', project: doc.project || '', report_type: doc.report_type || '' });
  const meta = version?.metadata || {};
  const dirty = ['title', 'project', 'report_type'].filter((k) => (form[k] || '') !== (doc[k] || ''));

  return (
    <div className="dd-details-grid">
      <section>
        <h4>Document</h4>
        <div className="dd-form">
          <label>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label>Project<input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} /></label>
          <label>Report type<input value={form.report_type} onChange={(e) => setForm({ ...form, report_type: e.target.value })} /></label>
        </div>
        <Button size="sm" disabled={dirty.length === 0} onClick={() => dirty.forEach((k) => onSave(k, form[k]))}>Save</Button>

        <h4>Detected from the Word file</h4>
        <table className="dd-meta-table">
          <tbody>
            {META_FIELDS.filter(([k]) => meta[k]).map(([k, label]) => (
              <tr key={k}>
                <th>{label}</th>
                <td>{String(meta[k].value)}</td>
                <td className="dd-muted">{meta[k].source}</td>
                <td><span className={`dd-conf ${confidenceClass(meta[k].confidence)}`} title={`Confidence ${meta[k].confidence}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h4>Page setup in source</h4>
        <ul className="dd-plain-list">
          {(version?.sections || []).map((s) => (
            <li key={s.index}>
              Section {s.index + 1}: {s.page_width_in}″ × {s.page_height_in}″ {s.orientation}
              <span className="dd-muted"> · blocks {s.start_seq}–{s.end_seq}</span>
            </li>
          ))}
        </ul>

        <h4>Versions</h4>
        <ul className="dd-plain-list">
          {doc.versions.map((v) => (
            <li key={v.id}>
              v{v.version_no} · {v.source_file_name} · {formatBytes(v.source_size_bytes)} ·{' '}
              <span className={`dd-status ${v.status}`}>{STATUS_LABELS[v.status] || v.status}</span>
              <span className="dd-muted"> · {v.created_by || 'unknown'} · {formatRelative(v.created_at)}{v.stats?.parse_seconds ? ` · parsed in ${v.stats.parse_seconds}s` : ''}</span>
            </li>
          ))}
        </ul>
        <Button size="sm" variant="secondary" onClick={onReparse}>Re-parse this version</Button>
        <p className="dd-muted">Review decisions are kept per block and carry over to new versions and re-parses.</p>

        {onDelete && (
          <div className="dd-danger-zone">
            <h4>Remove document</h4>
            <p className="dd-muted">Permanently deletes this report, all versions, review changes, layout photos, and stored files.</p>
            <Button size="sm" variant="ghost" className="dd-danger-button" onClick={onDelete}>Remove from list…</Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default DocumentReview;
